import { getErrorMessage } from '@/lib/functions';
import { formatAmountForDisplay, formatGroupedAmount, parseGroupedAmount } from '@/lib/amountFormat';
﻿import { useLocalSearchParams, useRouter } from 'expo-router';
import { useTranslation } from 'react-i18next';
import { useEffect, useMemo, useState } from 'react';
import {
  ActivityIndicator,
  KeyboardAvoidingView,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  View,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { Button } from '@/components/ui/Button';
import { DatePickerField } from '@/components/ui/DatePickerField';
import { Input } from '@/components/ui/Input';
import { DisclaimerNote } from '@/components/DisclaimerNote';
import { contentMaxWidth, fontSize, radius, spacing } from '@/constants/theme';
import type { AppColors } from '@/constants/theme';
import { useTheme } from '@/contexts/ThemeContext';
import type { Agreement, AgreementType, AmountUnit } from '@/lib/database.types';
import { getAgreement } from '@/lib/agreements';
import { fetchAgreementParties, type AgreementParty } from '@/lib/multiParty';
import { fetchProfilesByIds } from '@/lib/agreements';
import type { Profile } from '@/lib/database.types';
import { supabase } from '@/lib/supabase';

type Errors = { title?: string; amount?: string };

const EXPIRY_LABEL_KEYS = ['Λήξη συμφωνίας', 'Παράδοση έργου', 'Προθεσμία πληρωμής', 'Ημερομηνία έναρξης', 'Άλλο'] as const;

function getExpiryLabels(t: (key: string) => string): { key: string; label: string }[] {
  return [
    { key: EXPIRY_LABEL_KEYS[0], label: t('agreements.editExpiryLabelAgreementEnd') },
    { key: EXPIRY_LABEL_KEYS[1], label: t('agreements.editExpiryLabelDelivery') },
    { key: EXPIRY_LABEL_KEYS[2], label: t('agreements.editExpiryLabelPaymentDeadline') },
    { key: EXPIRY_LABEL_KEYS[3], label: t('agreements.editExpiryLabelStartDate') },
    { key: EXPIRY_LABEL_KEYS[4], label: t('agreements.editExpiryLabelOther') },
  ];
}

type AgrExt = Agreement & { start_date?: string | null; end_date?: string | null };

function parseAmount(raw: string): number | null | undefined {
  if (!raw.trim()) return null;
  const value = parseGroupedAmount(raw);
  if (value == null || !Number.isFinite(value) || value < 0) return undefined;
  return value;
}

export default function EditDraftScreen() {
  const { colors } = useTheme();
  const styles = useMemo(() => makeStyles(colors), [colors]);
  const { t } = useTranslation();
  const EXPIRY_LABELS = useMemo(() => getExpiryLabels(t), [t]);

  const { id } = useLocalSearchParams<{ id: string }>();
  const router = useRouter();
  const insets = useSafeAreaInsets();

  const [agreement, setAgreement] = useState<AgrExt | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<Errors>({});
  const [formError, setFormError] = useState<string | null>(null);

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [amount, setAmount] = useState('');
  const [amountUnit, setAmountUnit] = useState<AmountUnit>('eur');
  const [agreementType, setAgreementType] = useState<AgreementType>('one_off');
  const [expiryDate, setExpiryDate] = useState<Date | null>(null);
  const [expiryLabel, setExpiryLabel] = useState<string>(EXPIRY_LABEL_KEYS[0]);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [multiParties, setMultiParties] = useState<AgreementParty[]>([]);
  const [multiProfiles, setMultiProfiles] = useState<Record<string, Profile>>({});

  useEffect(() => {
    if (!id) return;
    void getAgreement(id).then(async (agr) => {
      if (!agr || agr.status !== 'draft') {
        router.back();
        return;
      }
      const ext = agr as AgrExt;
      setAgreement(ext);
      setTitle(ext.title);
      setDescription(ext.description ?? '');
      setAmount(ext.amount != null ? formatAmountForDisplay(ext.amount) : '');
      setAmountUnit(ext.amount_unit as AmountUnit);
      const type = (ext.type as AgreementType | undefined) ?? 'one_off';
      setAgreementType(type);
      if (type === 'fixed_term') {
        setStartDate(ext.start_date ? new Date(ext.start_date) : null);
        setEndDate(ext.end_date ? new Date(ext.end_date) : null);
      } else {
        setExpiryDate(ext.expires_at ? new Date(ext.expires_at) : null);
        setExpiryLabel(ext.expires_at_label ?? EXPIRY_LABEL_KEYS[0]);
      }
      const parties = await fetchAgreementParties(ext.id).catch(() => []);
      setMultiParties(parties);
      if (parties.length > 0) {
        setMultiProfiles(await fetchProfilesByIds(parties.map((p) => p.user_id).filter((id): id is string => !!id)));
      }
      setLoading(false);
    }).catch(() => { router.back(); });
  }, [id, router]);

  const isMultiParty = multiParties.length > 0;

  async function onSave() {
    setFormError(null);
    const next: Errors = {};
    if (!title.trim()) next.title = t('errors.titleRequired');
    const parsedAmount = parseAmount(amount);
    if (parsedAmount === undefined) next.amount = t('errors.invalidAmount');
    if (agreementType === 'fixed_term') {
      if (!startDate) next.title = next.title ?? t('agreements.editErrorStartDateRequired');
      if (!endDate) next.title = next.title ?? t('agreements.editErrorEndDateRequired');
      if (startDate && endDate && endDate <= startDate) next.title = t('agreements.editErrorEndAfterStart');
    }
    if (next.title || next.amount) { setErrors(next); return; }
    setErrors({});

    setSaving(true);
    try {
      const { error } = await supabase
        .from('agreements')
        .update({
          title: title.trim(),
          type: agreementType,
          description: description.trim() || null,
          amount: parsedAmount ?? null,
          amount_unit: amountUnit,
          expires_at: agreementType === 'one_off' && expiryDate
            ? (() => { const d = new Date(expiryDate); d.setHours(23, 59, 59, 0); return d.toISOString(); })()
            : null,
          expires_at_label: agreementType === 'one_off' && expiryDate ? expiryLabel : null,
          start_date: agreementType === 'fixed_term' && startDate ? startDate.toISOString().slice(0, 10) : null,
          end_date: agreementType === 'fixed_term' && endDate ? endDate.toISOString().slice(0, 10) : null,
        })
        .eq('id', id);
      if (error) throw new Error(error.message);
      router.back();
    } catch (e) {
      setFormError(getErrorMessage(e, t('agreements.editErrorSaveFailed')));
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <View style={styles.center}>
        <ActivityIndicator size="large" color={colors.primary} />
      </View>
    );
  }

  return (
    <KeyboardAvoidingView
      style={styles.flex}
      behavior={Platform.OS === 'ios' ? 'padding' : undefined}
    >
      <ScrollView
        contentContainerStyle={[
          styles.content,
          { paddingTop: insets.top + spacing.lg, paddingBottom: insets.bottom + spacing.lg },
        ]}
        keyboardShouldPersistTaps="handled"
      >
        <View style={styles.topBar}>
          <Pressable onPress={() => router.back()} hitSlop={12} accessibilityRole="button">
            <Text style={styles.back}>{t('common.back')}</Text>
          </Pressable>
        </View>

        <View style={styles.header}>
          <Text style={styles.title}>{t('agreements.action_editDraft')}</Text>
          <Text style={styles.subtitle}>
            {t('agreements.editSubtitle')}
          </Text>
        </View>

        <View style={styles.form}>
          <Input
            label={t('agreements.field_title')}
            value={title}
            onChangeText={setTitle}
            error={errors.title}
            placeholder={t('placeholder.agreementTitle')}
          />

          {/* Τύπος συμφωνίας (read-only label + fields) */}
          <View style={styles.field}>
            <Text style={styles.fieldLabel}>{t('agreements.editTypeLabel')}</Text>
            <View style={styles.typeRow}>
              {(['one_off', 'ongoing', 'fixed_term'] as AgreementType[]).map((opt) => {
                const sel = agreementType === opt;
                const label = opt === 'one_off' ? t('agreements.editTypeOneOff') : opt === 'ongoing' ? t('agreements.editTypeOngoing') : t('agreements.editTypeFixedTerm');
                return (
                  <Pressable
                    key={opt}
                    onPress={() => setAgreementType(opt)}
                    style={[styles.typeChip, sel && styles.typeChipSelected]}
                    accessibilityRole="button"
                  >
                    <Text style={[styles.typeChipText, sel && styles.typeChipTextSelected]}>{label}</Text>
                  </Pressable>
                );
              })}
            </View>
          </View>

          {/* Ημερομηνίες ανά τύπο */}
          {agreementType === 'one_off' && (
            <View style={styles.field}>
              <DatePickerField
                label={t('agreements.field_expiryDate')}
                value={expiryDate}
                onChange={setExpiryDate}
                minimumDate={new Date()}
              />
              <Text style={styles.hint}>
                {expiryDate ? t('hint.milestone') : t('hint.noMilestone')}
              </Text>
              {expiryDate && (
                <>
                  <Text style={styles.fieldLabel}>{t('agreements.field_dateType')}</Text>
                  <View style={styles.labelChips}>
                    {EXPIRY_LABELS.map((opt) => {
                      const sel = expiryLabel === opt.key;
                      return (
                        <Pressable
                          key={opt.key}
                          onPress={() => setExpiryLabel(opt.key)}
                          style={[styles.labelChip, sel && styles.labelChipSelected]}
                          accessibilityRole="button"
                        >
                          <Text style={[styles.labelChipText, sel && styles.labelChipTextSelected]}>
                            {opt.label}
                          </Text>
                        </Pressable>
                      );
                    })}
                  </View>
                </>
              )}
            </View>
          )}

          {agreementType === 'fixed_term' && (
            <View style={styles.field}>
              <Text style={styles.fieldLabel}>{t('agreements.editStartDateLabel')}</Text>
              <DatePickerField value={startDate} onChange={setStartDate} minimumDate={new Date()} />
              <Text style={[styles.fieldLabel, { marginTop: 4 }]}>{t('agreements.editEndDateLabel')}</Text>
              <DatePickerField value={endDate} onChange={setEndDate} minimumDate={startDate ?? new Date()} />
              {startDate && endDate && (
                <Text style={styles.hint}>
                  {t('agreements.editDurationHint', { days: Math.round((endDate.getTime() - startDate.getTime()) / 86400000) })}
                </Text>
              )}
            </View>
          )}

          {agreementType === 'ongoing' && (
            <Text style={styles.hint}>{t('agreements.editOngoingHint')}</Text>
          )}

          {!!agreement?.counterparty_id && (
            <View style={styles.field}>
              <Text style={styles.fieldLabel}>{t('agreements.field_counterparty')}</Text>
              <View style={styles.readonly}>
                <Text style={styles.readonlyText}>{agreement.counterparty_email ?? t('common.selectedUser')}</Text>
              </View>
            </View>
          )}

          {isMultiParty && (
            <View style={styles.field}>
              <Text style={styles.fieldLabel}>{t('agreements.editPartiesLabel')}</Text>
              {multiParties.map((mp) => (
                <View key={mp.user_id} style={styles.readonly}>
                  <Text style={styles.readonlyText}>
                    {mp.role === 'creator' ? t('agreements.editYouCreator') : ((mp.user_id ? multiProfiles[mp.user_id]?.full_name : null)?.trim() || t('agreements.editUserFallback'))} — {mp.share_percent}%
                  </Text>
                </View>
              ))}
              <Text style={styles.hint}>{t('agreements.editPartiesHint')}</Text>
            </View>
          )}

          <View style={styles.field}>
            <Text style={styles.fieldLabel}>
              {isMultiParty ? t('agreements.editMultiAmountLabel') : (amountUnit === 'percent' ? t('agreements.field_percentOptional') : t('agreements.field_amountOptional'))}
            </Text>
            <View style={[styles.amountRow, !!errors.amount && styles.amountRowError]}>
              <TextInput
                style={styles.amountInput}
                value={amount}
                onChangeText={(v) => setAmount(formatGroupedAmount(v))}
                placeholder={amountUnit === 'percent' ? t('agreements.editAmountPercentPlaceholder') : t('agreements.editAmountEurPlaceholder')}
                placeholderTextColor={colors.textMuted}
                keyboardType="decimal-pad"
              />
              {!isMultiParty && (
                <View style={styles.unitToggle}>
                  {(['eur', 'percent'] as AmountUnit[]).map((u) => {
                    const sel = amountUnit === u;
                    return (
                      <Pressable
                        key={u}
                        onPress={() => setAmountUnit(u)}
                        style={[styles.unitChip, sel && styles.unitChipSelected]}
                        accessibilityRole="button"
                      >
                        <Text style={[styles.unitChipText, sel && styles.unitChipTextSelected]}>
                          {u === 'eur' ? '€' : '%'}
                        </Text>
                      </Pressable>
                    );
                  })}
                </View>
              )}
            </View>
            {!!errors.amount && <Text style={styles.formError}>{errors.amount}</Text>}
            {!isMultiParty && amountUnit === 'percent' && !!amount.trim() && (
              <Text style={styles.hint}>
                {t('agreements.editCounterpartyShareHint', {
                  percent: Math.max(0, Math.min(100, Math.round((100 - (parseGroupedAmount(amount) ?? 0)) * 100) / 100)),
                })}
              </Text>
            )}
          </View>

          <View style={styles.field}>
            <Input
              label={t('agreements.field_termsOptional')}
              value={description}
              onChangeText={setDescription}
              placeholder={t('placeholder.agreementTerms')}
              multiline
              numberOfLines={4}
              style={styles.multiline}
            />
          </View>

          {!!formError && <Text style={styles.formError}>{formError}</Text>}

          <Button title={t('common.save')} icon="save-outline" onPress={onSave} loading={saving} />
          <DisclaimerNote />
        </View>
      </ScrollView>
    </KeyboardAvoidingView>
  );
}

function makeStyles(colors: AppColors) {
  return StyleSheet.create({
    flex: { flex: 1, backgroundColor: colors.background },
    center: { flex: 1, alignItems: 'center', justifyContent: 'center', backgroundColor: colors.background },
    content: {
      // Σε μεγάλες οθόνες η φόρμα μένει σε αναγνώσιμο πλάτος αντί να
      // τεντώνεται· σε κινητό το maxWidth δεν ενεργοποιείται ποτέ.
      width: '100%',
      maxWidth: contentMaxWidth.form,
      alignSelf: 'center',
      flexGrow: 1,
      paddingHorizontal: spacing.lg,
      gap: spacing.lg,
    },
    topBar: { flexDirection: 'row' },
    back: { color: colors.primaryLight, fontSize: fontSize.md, fontWeight: '600' },
    header: { gap: spacing.sm },
    title: { color: colors.text, fontSize: fontSize.xl, fontWeight: '700' },
    subtitle: { color: colors.textMuted, fontSize: fontSize.md },
    form: { gap: spacing.md },
    field: { gap: spacing.xs },
    fieldLabel: { color: colors.textMuted, fontSize: fontSize.sm, fontWeight: '500' },
    hint: { color: colors.textMuted, fontSize: fontSize.sm },
    typeRow: { flexDirection: 'row', gap: spacing.sm },
    typeChip: {
      flex: 1, alignItems: 'center', borderWidth: 1,
      borderColor: colors.border, borderRadius: radius.md, paddingVertical: spacing.md,
    },
    typeChipSelected: { backgroundColor: colors.primary, borderColor: colors.primary },
    typeChipText: { color: colors.textMuted, fontSize: fontSize.md, fontWeight: '600' },
    typeChipTextSelected: { color: colors.textOnPrimary },
    amountRow: {
      flexDirection: 'row', alignItems: 'center', gap: spacing.sm, height: 52,
      backgroundColor: colors.surface, borderWidth: 1, borderColor: colors.border,
      borderRadius: radius.md, paddingLeft: spacing.md, paddingRight: spacing.xs,
    },
    amountRowError: { borderColor: colors.danger },
    amountInput: { flex: 1, color: colors.text, fontSize: fontSize.md },
    unitToggle: { flexDirection: 'row', gap: 4 },
    unitChip: {
      minWidth: 40, alignItems: 'center', borderRadius: radius.sm,
      borderWidth: 1, borderColor: colors.border, paddingVertical: 6, paddingHorizontal: spacing.sm,
    },
    unitChipSelected: { backgroundColor: colors.primary, borderColor: colors.primary },
    unitChipText: { color: colors.textMuted, fontSize: fontSize.md, fontWeight: '700' },
    unitChipTextSelected: { color: colors.textOnPrimary },
    readonly: {
      height: 52, justifyContent: 'center', backgroundColor: colors.surfaceAlt,
      borderWidth: 1, borderColor: colors.border, borderRadius: radius.md, paddingHorizontal: spacing.md,
    },
    readonlyText: { color: colors.text, fontSize: fontSize.md, fontWeight: '600' },
    labelChips: { flexDirection: 'row', flexWrap: 'wrap', gap: spacing.xs, marginTop: spacing.xs },
    labelChip: {
      borderWidth: 1, borderColor: colors.border, borderRadius: radius.full,
      paddingVertical: 5, paddingHorizontal: spacing.sm,
    },
    labelChipSelected: { backgroundColor: colors.primary, borderColor: colors.primary },
    labelChipText: { color: colors.textMuted, fontSize: fontSize.sm, fontWeight: '600' },
    labelChipTextSelected: { color: colors.textOnPrimary },
    multiline: { height: 110, paddingTop: spacing.md, textAlignVertical: 'top' },
    formError: { color: colors.danger, fontSize: fontSize.sm },
  });
}
